import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { MailCheck, ShieldCheck, Loader2, CheckCircle2, XCircle } from "lucide-react";
import Navbar from "@/components/Navbar";

export default function VerifyContactChange() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("loading"); // loading | ready | done | already | expired | error
  const [info, setInfo] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState("error"); setMsg("This verification link is missing its token."); return; }
    api.get(`/contact-change/verify/preview?token=${encodeURIComponent(token)}`)
      .then(({ data }) => {
        if (data.status === "already_applied") { setState("already"); }
        else if (data.status === "expired") { setState("expired"); }
        else { setInfo(data); setState("ready"); }
      })
      .catch((err) => { setState("error"); setMsg(formatError(err.response?.data?.detail)); });
  }, [token]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/contact-change/verify", { token });
      if (data.status === "already_applied") { setState("already"); }
      else { setState("done"); setMsg(data.message || "Your new email is verified and active."); }
    } catch (err) {
      setState("error"); setMsg(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen text-foreground" data-testid="verify-contact-change-page">
      <Navbar />
      <div className="flex items-center justify-center px-4 min-h-[calc(100vh-4rem)]">
      <div className="w-full max-w-md border border-sniper bg-surface p-7">
        <div className="flex items-center gap-2 mb-1">
          <MailCheck className="w-6 h-6 text-olive-bright" />
          <h1 className="font-heading text-2xl uppercase tracking-tight">Confirm your email</h1>
        </div>

        {state === "loading" && (
          <p className="font-mono text-xs uppercase text-muted-foreground mt-4 inline-flex items-center gap-2" data-testid="verify-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking your link…
          </p>
        )}

        {state === "ready" && (
          <div className="mt-3" data-testid="verify-ready">
            <p className="text-sm font-body text-secondary-foreground">
              Confirm this address to finish the change on your OpticSwap account. Once confirmed it
              becomes your account email and will receive password resets.
            </p>
            <div className="mt-4 border border-sniper bg-input px-3 py-2.5 font-mono text-sm text-foreground break-all" data-testid="verify-new-email">
              {info?.new_value}
            </div>
            <button onClick={confirm} disabled={busy} data-testid="verify-confirm-btn"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-sm px-5 py-3 transition-colors">
              <ShieldCheck className="w-4 h-4" /> {busy ? "Confirming…" : "Confirm this email"}
            </button>
            <p className="font-mono text-[10px] uppercase text-muted-foreground mt-3">
              Didn't request this? Ignore this page — nothing changes until you confirm.
            </p>
          </div>
        )}

        {state === "done" && (
          <div className="mt-4" data-testid="verify-done">
            <p className="text-sm font-body text-olive-bright inline-flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 shrink-0" /> {msg}
            </p>
            <Link to="/login" className="mt-5 inline-block font-heading font-bold uppercase text-sm bg-olive hover:bg-olive-bright text-white px-5 py-3 transition-colors" data-testid="verify-login-link">
              Go to sign in
            </Link>
          </div>
        )}

        {state === "already" && (
          <p className="text-sm font-body text-secondary-foreground mt-4 inline-flex items-start gap-2" data-testid="verify-already">
            <CheckCircle2 className="w-5 h-5 text-olive-bright shrink-0" /> This change was already applied. Your email is up to date.
          </p>
        )}

        {(state === "expired" || state === "error") && (
          <p className="text-sm font-body text-blaze mt-4 inline-flex items-start gap-2" data-testid="verify-error">
            <XCircle className="w-5 h-5 shrink-0" /> {state === "expired"
              ? "This verification link has expired. Ask an admin to re-issue the change."
              : (msg || "This link is invalid.")}
          </p>
        )}
      </div>
      </div>
    </div>
  );
}
